/* eslint-disable no-param-reassign */
import React from 'react';
import { TextInput } from '../../store/inputs/TextInput';
import { NumberInput } from '../../store/inputs/NumberInput';
import { BooleanInput } from '../../store/inputs/BooleanInput';
import { DateInput } from '../../store/inputs/DateInput';
import { OAuthCredentialInput } from '../../store/inputs/OAuthCredentialInput';
import CBooleanInput from '../../components/Forms/CBooleanInput';
import CTextInput from '../../components/Forms/CTextInput';
import CDateInput from '../../components/Forms/CDateInput';
import CNumberInput from '../../components/Forms/CNumberInput';
import CDropDownInput from '../../components/Forms/CDropDownInput';
import COAuthCredentialInput from '../../components/Forms/COAuthCredentialInput';
import CDropDownListInput from '../../components/Forms/CDropDownListInput';
import CAssetInput from '../../components/Forms/CAssetInput';
import CAnyAssetInput from '../../components/Forms/CAnyAssetInput';
import CAssetListInput from '../../components/Forms/CAssetListInput';
import CTextListInput from '../../components/Forms/CTextListInput';
import CNumberListInput from '../../components/Forms/CNumberListInput';
import CDateListInput from '../../components/Forms/CDateListInput';
import CPropListInput from '../../components/Forms/CPropListInput';
import { AssetInput } from '../../store/inputs/AssetInput';
import { AnyAssetInput } from '../../store/inputs/AnyAssetInput';
import { TextListInput } from '../../store/inputs/TextListInput';
import { AssetListInput } from '../../store/inputs/AssetListInput';
import { NumberListInput } from '../../store/inputs/NumberListInput';
import { PropListInput } from '../../store/inputs/PropListInput';
import { DateListInput } from '../../store/inputs/DateListInput';
import {
  InputTypes,
  NumberInputType,
  PropListType,
  InputClass,
} from '../../store/inputs/types';
import CXYTCredentialInput from '../../components/Forms/CXYTCredentialInput';
import { XYTCredentialInput } from '../../store/inputs/XYTCredentialInput';
import { DataTypeList } from '../../types/DataTypeList';

export const fromJsonToFormContext = (
  f: InputTypes,
  tag: string,
  dtList: DataTypeList,
): InputTypes => {
  f.tag = tag;
  const defaultOnChange = (v: any) => {};
  const defaultNumberOnChange = (v: string | number | null | undefined) => {};
  const onChange = f.dataType === 'number' || (f as NumberInputType).dropDownValues ? defaultNumberOnChange : defaultOnChange;
  const clearValue = () => {};
  const toggleDisabled = () => {};
  f = { ...f, onChange, clearValue, toggleDisabled } as InputClass;
  if (f.dataType === 'number' || (f as NumberInputType).dropDownValues) {
    f = { ...f, onChange: defaultNumberOnChange } as NumberInputType;
  }
  f.clearValue = f.clearValue || (() => {});
  f.toggleDisabled = f.toggleDisabled || (() => {});

  switch (f.tag) {
    case 'openIDSub':
    case 'openIDToken':
      return new OAuthCredentialInput(f);
    case 'xytMinutiae':
      return new XYTCredentialInput(f);
    default:
      break;
  }

  switch (f.dataType) {
    case '[]string':
      return new TextListInput(f);
    case '[]number':
      return new NumberListInput(f);
    case '[]datetime':
      return new DateListInput(f);
    case '[]@prop':
      return new PropListInput({ ...f, props: (f as PropListType).props });
    default:
      break;
  }

  if (f.dataType.indexOf('[]') >= 0) {
    if ((f as NumberInputType).dropDownValues) {
      return new NumberListInput({ ...f, dataType: '[]number' });
    }
    return new AssetListInput(f);
  }

  switch (f.dataType) {
    case 'boolean':
      return new BooleanInput(f);
    case 'string':
      return new TextInput({ ...f, onChange, clearValue, toggleDisabled } );
    case 'number':
      return new NumberInput({ ...f, onChange, clearValue, toggleDisabled });
    case 'datetime':
      return new DateInput({ ...f, onChange, clearValue, toggleDisabled });
    case '@key':
      return new AnyAssetInput(f);
    default:
      if (f.dataType.indexOf('->') >= 0) {
        f.dataType = f.dataType.replace('->', '');
        return new AssetInput(f);
      }

      if ((f as NumberInputType).dropDownValues) {
        return new NumberInput({ ...f, dataType: 'number', onChange, clearValue, toggleDisabled });
      }

      const dtype = dtList.find(d => d.dataType === f.dataType)
      if (dtype && dtype.acceptedFormats && dtype.acceptedFormats === 'string') {
        return new TextInput({ ...f, dataType: 'string', onChange, clearValue, toggleDisabled });
      } else if (dtype && dtype.acceptedFormats && dtype.acceptedFormats === 'number') {
        return new NumberInput({ ...f, dataType: 'number', onChange, clearValue, toggleDisabled });
      }

      return new TextInput({ ...f, dataType: 'string', onChange, clearValue, toggleDisabled });
  }
};

export const getForm = (
  item: InputTypes,
  dataTypeList: DataTypeList,
): JSX.Element => {
  switch (item.tag) {
    case 'openIDSub':
    case 'openIDToken':
      return (
        <COAuthCredentialInput
          key={item.tag}
          item={item as OAuthCredentialInput}
        />
      );
    case 'xytMinutiae':
      return (
        <CXYTCredentialInput key={item.tag} item={item as XYTCredentialInput} />
      );
    default:
      break;
  }

  switch (item.dataType) {
    case '[]string':
      return <CTextListInput key={item.tag} item={item as TextListInput} />;
    case '[]number':
      if ((item as NumberInput).dropDownValues) {
        return (
          <CDropDownListInput key={item.tag} item={item as NumberListInput} />
        );
      }
      return <CNumberListInput key={item.tag} item={item as NumberListInput} />;
    case '[]datetime':
      return <CDateListInput key={item.tag} item={item as DateListInput} />;
    case '[]@prop':
      return <CPropListInput key={item.tag} item={item as PropListInput} />;
    default:
      break;
  }

  if (item.dataType.indexOf('->') >= 0 || item.dataType.indexOf('-%3E') >= 0) {
    item.dataType = item.dataType.replace('->', '').replace('-%3E', '');
  }

  if (item.dataType.startsWith('->') && item.dataType.indexOf('[]') >= 0) {
    return <CAssetListInput key={item.tag} item={item as AssetListInput} />;
  }


  if (item.dataType.indexOf('[]') >= 0) {
    if ((item as NumberInput).dropDownValues) {
      return (
        <CDropDownListInput key={item.tag} item={item as NumberListInput} />
      );
    }
    return <CAssetListInput key={item.tag} item={item as AssetListInput} />;
  }


  switch (item.dataType) {
    case 'boolean':
      return <CBooleanInput key={item.tag} item={item as BooleanInput} />;
    case 'string':
      return <CTextInput key={item.tag} item={item as TextInput} />;
    case 'number':
      if ((item as NumberInput).dropDownValues) {
        return <CDropDownInput key={item.tag} item={item as NumberInput} />;
      }
      return <CNumberInput key={item.tag} item={item as NumberInput} />;
    case 'datetime':
      return <CDateInput key={item.tag} item={item as DateInput} />;
    case '@key':
      return <CAnyAssetInput key={item.tag} item={item as AnyAssetInput} />;
    default:
      if (item.dataType.startsWith('->')) {
        return <CAssetInput key={item.tag} item={item as AssetInput} />;
      }

      if (item.dataType.indexOf('[]') >= 0) {
        return <CAssetListInput key={item.tag} item={item as AssetListInput} />;
      }

      if (!dataTypeList.some((dt) => dt.dataType === item.dataType)) {
        return <CAssetInput key={item.tag} item={item as AssetInput} />;
      }

      return <CTextInput key={item.tag} item={item as TextInput} />;
  }
};
